import { Stack, Text, Box, Skeleton, Flex, CopyButton, ActionIcon, Divider, Indicator } from '@mantine/core'
import { IconCopy, IconCheck, IconUserPlus } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { InviteCodeTabProps } from './interfaces'
import { useStyles } from './AddStudentsModalStyles'

const column = { flex: 1 }
const hideOnMobile = { '@media (max-width: 768px)': { display: 'none' as const } }

export function InviteCodeTab({ inviteCode, loading }: InviteCodeTabProps) {
  const { t } = useTranslation()
  const { classes } = useStyles()

  if (loading) return <InviteCodeSkeleton />
  if (!inviteCode) return null

  return (
    <Flex gap={40} direction={{ base: 'column', sm: 'row' }}>
      <Stack spacing='xs' sx={column}>
        <Text fw={700} size='lg'>{t('add_students_modal.step1_title')}</Text>
        <Text size='sm' lh={1.6} color='dimmed'>{t('add_students_modal.step1_link_text')}</Text>

        <Flex align='center' gap={6} wrap='wrap'>
          <Text c='violet' component='a' href={inviteCode.url} target='_blank' size='sm' fw={500} sx={{ wordBreak: 'break-all' }}>
            {inviteCode.url}
          </Text>
          <CopyButton value={inviteCode.url}>
            {({ copied, copy }) => (
              <ActionIcon variant='subtle' color={copied ? 'turquoise' : 'gray'} size='sm' onClick={copy}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            )}
          </CopyButton>
        </Flex>

        <Text size='sm' lh={1.6} color='dimmed'>{t('add_students_modal.step1_code_text')}</Text>

        <Box className={classes.inviteCodeBox}>
          <Text c='dimmed' size='xs' tt='uppercase' fw={700}>{t('add_students_modal.invite_code_label')}</Text>
          <Text className={classes.inviteCode}>{inviteCode.code}</Text>
        </Box>
      </Stack>

      <Divider orientation='vertical' sx={hideOnMobile} />

      <Stack spacing='xs' sx={column}>
        <Text fw={700} size='lg'>{t('add_students_modal.step2_title')}</Text>
        <Text size='sm' lh={1.6} color='dimmed'>{t('add_students_modal.step2_review_text')}</Text>
        <ReviewBadgeIllustration />
      </Stack>
    </Flex>
  )
}

function InviteCodeSkeleton() {
  return (
    <Flex gap={40} direction={{ base: 'column', sm: 'row' }}>
      <Stack spacing='xs' sx={column}>
        <Skeleton height={24} width='25%' animate />
        <Skeleton height={14} width='85%' animate />
        <Skeleton height={14} width='60%' animate />
        <Skeleton height={110} radius='md' mt='xs' animate />
      </Stack>
      <Divider orientation='vertical' sx={hideOnMobile} />
      <Stack spacing='xs' sx={column}>
        <Skeleton height={24} width='25%' animate />
        <Skeleton height={14} width='90%' animate />
        <Skeleton height={140} radius='md' mt='xs' animate />
      </Stack>
    </Flex>
  )
}

function ReviewBadgeIllustration() {
  return (
    <Box mt='xs' sx={theme => ({
      background: 'linear-gradient(135deg, #495bdf, #6C63FF)',
      borderRadius: theme.radius.lg,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
    })}>
      <Box sx={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', top: -60, left: -40 }} />
      <Box sx={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: -30, right: -20 }} />

      <Indicator color='red' size={18} offset={4} label='1' zIndex={2}>
        <Box sx={{
          width: 52, height: 52, borderRadius: '50%',
          backgroundColor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconUserPlus size={26} color='#495057' />
        </Box>
      </Indicator>
    </Box>
  )
}
